import { useQuery } from '@apollo/client';
import { Ionicons } from '@expo/vector-icons';
import React, { useMemo } from 'react';
import {
	ScrollView,
	TouchableOpacity,
	useWindowDimensions,
	View,
} from 'react-native';

import { useUserContext } from '../../../common/utils/context';
import { getLocalTime } from '../../../common/utils/time/time';
import { getUserName } from '../../../common/utils/user/getUserName';
import { formatDate } from '../../../common/utils/utils';
import {
	Dialog,
	ErrorCallout,
	Heading,
	Spinner,
	Text,
} from '../../components';
import { Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useTheme } from '../../theme';
import { GET_MY_DAY_EMPLOYEE_RUNS } from './driverMyDayQueries';

const getEmployeeName = (employee) => {
	const name = getUserName(employee);
	return ['No user', 'Un-named user'].includes(name)
		? employee?.username || 'Unknown employee'
		: name;
};

const EmployeeMyDaySelectorDialog = ({
	isOpen,
	canEngage,
	onClose,
	onSelect,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const { height } = useWindowDimensions();
	const { tenant, user } = useUserContext();
	const timezone = tenant?.timezone;
	const todayDateKey = useMemo(() => getLocalTime(timezone).format('YYYY-MM-DD'), [timezone]);
	const queryFilter = useMemo(() => ({
		// Keep the buffered query and exact state-local match for compatibility
		// with older server releases that resolve these filters in server-local time.
		plannedFromDate: getLocalTime(timezone).subtract(1, 'day').startOf('day').unix(),
		plannedToDate: getLocalTime(timezone).add(1, 'day').endOf('day').unix(),
	}), [timezone]);
	const { data, loading, error } = useQuery(GET_MY_DAY_EMPLOYEE_RUNS, {
		variables: { filter: queryFilter },
		skip: !isOpen,
		// Employee previews must never populate the signed-in user's offline My Day cache.
		fetchPolicy: 'no-cache',
		errorPolicy: 'all',
	});
	const employees = useMemo(() => {
		const employeeById = new Map();

		(data?.getServiceRuns?.items || []).forEach((run) => {
			const employee = run?.driver;
			const employeeId = employee?.id ? String(employee.id) : null;
			const plannedDateKey = run?.planned
				? getLocalTime(timezone, run.planned).format('YYYY-MM-DD')
				: null;
			if (!employeeId || employeeId === String(user?.id) || plannedDateKey !== todayDateKey) {
				return;
			}

			if (!employeeById.has(employeeId)) {
				employeeById.set(employeeId, {
					...employee,
					id: employeeId,
					runIds: [],
					routeNames: new Set(),
					dateKey: todayDateKey,
				});
			}

			const entry = employeeById.get(employeeId);
			entry.runIds.push(String(run.id));
			if (run?.route?.name) {
				entry.routeNames.add(run.route.name);
			}
		});

		return Array.from(employeeById.values())
			.map(employee => ({
				...employee,
				routeNames: Array.from(employee.routeNames),
			}))
			.sort((employeeA, employeeB) => (
				getEmployeeName(employeeA).localeCompare(getEmployeeName(employeeB))
			));
	}, [data?.getServiceRuns?.items, timezone, todayDateKey, user?.id]);
	const todayDisplayDate = formatDate(getLocalTime(timezone), 'DAY_MONTHWORD_YEAR');

	return (
		<Dialog isOpen={isOpen} onClose={onClose}>
			<View style={styles.dialogContent}>
				<Heading
					heading="View an employee's day"
					subHeading={todayDisplayDate}
				/>
				<Text style={styles.introText}>
					{canEngage
						? 'Choose an employee with a run today. Review first, then start training on a run when you are ready.'
						: 'Choose an employee with a run today to review their recorded day read-only.'}
				</Text>
				{Boolean(error) && <ErrorCallout text="Some employee runs could not be loaded. Available results are shown below." />}
				{
					loading && !data
						? <Spinner />
						: (
							<ScrollView style={[styles.employeeList, { maxHeight: Math.max(220, height * 0.58) }]}>
								{
									employees.map(employee => (
										<TouchableOpacity
											key={employee.id}
											style={styles.employeeRow}
											activeOpacity={0.86}
											onPress={() => onSelect(employee)}
										>
											<View style={styles.employeeTextWrap}>
												<Text style={styles.employeeName}>{getEmployeeName(employee)}</Text>
												<Text style={styles.employeeRoutes}>
													{employee.routeNames.length ? employee.routeNames.join(' · ') : 'Routeless service run'}
												</Text>
											</View>
											<Tag
												label={`${employee.runIds.length} ${employee.runIds.length === 1 ? 'run' : 'runs'}`}
												style={styles.runCount}
											/>
											<Ionicons name="chevron-forward" size={16} color={theme.text2} />
										</TouchableOpacity>
									))
								}
								{
									!employees.length && (
										<View style={styles.emptyState}>
											<Text style={styles.emptyTitle}>
												{error ? 'Employee runs unavailable' : 'No employee runs today'}
											</Text>
											<Text style={styles.emptyText}>
												{error
													? 'Close this window and try again when the connection is available.'
													: 'No other employees have runs you can view for today.'}
											</Text>
										</View>
									)
								}
							</ScrollView>
						)
				}
			</View>
		</Dialog>
	);
};

const createStyles = (theme) => ({
	dialogContent: {
		minWidth: 260,
	},
	introText: {
		color: theme.text2,
		marginBottom: 12,
	},
	employeeList: {
		borderTopWidth: 1,
		borderTopColor: theme.border3,
	},
	employeeRow: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingVertical: 12,
		borderBottomWidth: 1,
		borderBottomColor: theme.border3,
	},
	employeeTextWrap: {
		flex: 1,
		minWidth: 0,
		paddingRight: 8,
	},
	employeeName: {
		color: theme.text1,
		fontSize: 15,
		fontWeight: '700',
	},
	employeeRoutes: {
		color: theme.text2,
		fontSize: 12,
		marginTop: 2,
	},
	runCount: {
		marginBottom: 0,
		marginRight: 6,
	},
	emptyState: {
		backgroundColor: theme.mainBg3,
		borderRadius: 8,
		padding: 14,
		marginTop: 12,
	},
	emptyTitle: {
		color: theme.text1,
		fontSize: 15,
		fontWeight: '700',
	},
	emptyText: {
		color: theme.text2,
		fontSize: 13,
		marginTop: 4,
	},
});

export default EmployeeMyDaySelectorDialog;
